import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2, Calendar, Eye, EyeOff,
  ArrowUp, ArrowDown, ChevronRight, Search,
  Wallet, Trash2, Car, Package, ToggleLeft, ToggleRight, ShoppingCart
} from 'lucide-react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine, ResponsiveContainer,
  Area
} from 'recharts'
import api from '../services/api'
import { useTheme } from '../hooks/useTheme'
import { useLanguage } from '../hooks/useLanguage'
import { useWorkspace } from '../hooks/useWorkspace'

// ── Types ──
interface CashFlowItem {
  category_name: string
  amount: number
  transaction_count: number
}

interface CashFlowStatement {
  period_start: string
  period_end: string
  opening_balance: number
  closing_balance: number
  operating: {
    inflows: CashFlowItem[]
    outflows: CashFlowItem[]
    total_inflows: number
    total_outflows: number
    net: number
  }
  investing: {
    inflows: CashFlowItem[]
    outflows: CashFlowItem[]
    total_inflows: number
    total_outflows: number
    net: number
  }
  financing: {
    inflows: CashFlowItem[]
    outflows: CashFlowItem[]
    total_inflows: number
    total_outflows: number
    net: number
  }
  net_cash_flow: number
  cash_change_percent: number
}

interface CashFlowPeriod {
  period: string
  statement: CashFlowStatement
}

interface CashFlowKPIs {
  operating_cash_flow: number
  free_cash_flow: number
  cash_burn_rate: number
  runway_months: number
}

interface CashFlowReportResponse {
  periods: CashFlowPeriod[]
  summary: CashFlowStatement
  kpis: CashFlowKPIs
}

// ── Helpers ──
const MONTHS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default function Treasury() {
  const { isDark } = useTheme()
  const { language } = useLanguage()
  const { currentWorkspace } = useWorkspace()
  const months = language === 'it' ? MONTHS_IT : MONTHS_EN

  // ── State ──
  const [showAmounts, setShowAmounts] = useState(true)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(['entrate', 'uscite']))
  const [categorySearch, setCategorySearch] = useState('')
  const [hoveredMonth, setHoveredMonth] = useState<string | null>(null)

  // ── Hypothetical scenarios ──
  interface HypotheticalScenario {
    id: string
    name: string
    amount: number
    month: string
    enabled: boolean
  }
  const [scenarios, setScenarios] = useState<HypotheticalScenario[]>([])
  const [showScenarioPanel, setShowScenarioPanel] = useState(false)

  const SCENARIO_PRESETS = language === 'it'
    ? [
        { icon: Car, label: 'Acquisto auto', amount: -25000 },
        { icon: Package, label: 'Acquisto attrezzature', amount: -5000 },
        { icon: ShoppingCart, label: 'Acquisto altro', amount: -3000 },
        { icon: ArrowUp, label: 'Entrata straordinaria', amount: 10000 },
      ]
    : [
        { icon: Car, label: 'Buy vehicle', amount: -25000 },
        { icon: Package, label: 'Buy equipment', amount: -5000 },
        { icon: ShoppingCart, label: 'Other purchase', amount: -3000 },
        { icon: ArrowUp, label: 'Extraordinary income', amount: 10000 },
      ]

  const addScenario = (name: string, amount: number) => {
    const nextMonth = new Date()
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    const month = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`
    setScenarios(prev => [...prev, {
      id: crypto.randomUUID(),
      name,
      amount,
      month,
      enabled: true,
    }])
    setShowScenarioPanel(true)
  }

  const removeScenario = (id: string) => {
    setScenarios(prev => prev.filter(s => s.id !== id))
  }

  const toggleScenario = (id: string) => {
    setScenarios(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s))
  }

  const updateScenario = (id: string, field: keyof HypotheticalScenario, value: string | number) => {
    setScenarios(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s))
  }

  // Date range: default 12 months back + 3 months forward
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date()
    const start = new Date(now.getFullYear() - 1, now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 3, 0)
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    }
  })

  const axisColor = isDark ? '#9ca3af' : '#6b7280'

  // ── Data ──
  const { data: cfData, isLoading } = useQuery<CashFlowReportResponse>({
    queryKey: ['treasury-cash-flow', dateRange, currentWorkspace?.id],
    queryFn: async () => {
      const r = await api.get(`/reports/cash-flow?from=${dateRange.start}&to=${dateRange.end}&group_by=month`)
      return r.data
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  // ── Computed ──
  const periods = useMemo(() => cfData?.periods || [], [cfData?.periods])

  const chartData = useMemo(() => {
    const activeScenarios = scenarios.filter(s => s.enabled)
    let cumulativeImpact = 0
    return periods.map(p => {
      const [y, m] = p.period.split('-')
      const totalInflows = p.statement.operating.total_inflows + p.statement.investing.total_inflows + p.statement.financing.total_inflows
      const totalOutflows = Math.abs(p.statement.operating.total_outflows + p.statement.investing.total_outflows + p.statement.financing.total_outflows)
      // Sum all active scenario impacts for this month
      const monthImpact = activeScenarios
        .filter(s => s.month === p.period)
        .reduce((sum, s) => sum + s.amount, 0)
      cumulativeImpact += monthImpact
      return {
        period: p.period,
        label: `${months[parseInt(m) - 1]} ${y.slice(2)}`,
        shortLabel: months[parseInt(m) - 1].toUpperCase(),
        fullLabel: `${months[parseInt(m) - 1]} 20${y.slice(2)}`,
        inflows: totalInflows,
        outflows: -totalOutflows,
        cashPosition: p.statement.closing_balance,
        scenarioPosition: activeScenarios.length > 0 ? p.statement.closing_balance + cumulativeImpact : undefined,
        openingBalance: p.statement.opening_balance,
      }
    })
  }, [periods, months, scenarios])

  // Current month index for highlight
  const currentMonthStr = (() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })()

  const formatCurrency = (v: number) => {
    if (!showAmounts) return '•••.•••'
    return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(v)
  }

  const formatCompact = (v: number) => {
    if (!showAmounts) return '•••'
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
    if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k`
    return v.toFixed(0)
  }

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Current treasury value
  const currentTreasury = periods.length > 0
    ? periods.find(p => p.period === currentMonthStr)?.statement.closing_balance
      ?? periods[periods.length - 1].statement.closing_balance
    : 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  return (
    <div className="space-y-4 pt-1">
      {/* ── Page title ── */}
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
          {language === 'it' ? 'Tesoreria' : 'Treasury'}
        </h1>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1">
          <Wallet className="w-3 h-3" />
          {language === 'it' ? 'Tesoreria attuale' : 'Current treasury'}
        </p>
      </div>

      {/* ── Top toolbar — Agicap-style ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
        {/* Left: treasury value */}
        <div className="flex items-center gap-4">
          <div>
            <span className={`text-lg font-bold tabular-nums ${currentTreasury >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {currentTreasury >= 0 ? '+' : ''}{formatCurrency(currentTreasury)} €
            </span>
          </div>
        </div>

        {/* Right: controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range */}
          <div className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 text-xs">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <input
              type="month"
              value={dateRange.start.slice(0, 7)}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value + '-01' }))}
              className="bg-transparent border-none p-0 text-xs focus:ring-0 text-gray-700 dark:text-gray-200 w-24"
            />
            <span className="text-gray-400">→</span>
            <input
              type="month"
              value={dateRange.end.slice(0, 7)}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-')
                const last = new Date(parseInt(y), parseInt(m), 0).getDate()
                setDateRange(prev => ({ ...prev, end: `${e.target.value}-${last}` }))
              }}
              className="bg-transparent border-none p-0 text-xs focus:ring-0 text-gray-700 dark:text-gray-200 w-24"
            />
          </div>

          {/* Privacy toggle */}
          <button
            onClick={() => setShowAmounts(!showAmounts)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            title={showAmounts ? 'Nascondi importi' : 'Mostra importi'}
          >
            {showAmounts ? <Eye className="w-4 h-4 text-gray-500" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
          </button>
        </div>
      </div>

      {/* ── Hypothetical Scenarios Panel ── */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl">
        <button
          onClick={() => setShowScenarioPanel(!showScenarioPanel)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors rounded-xl"
        >
          <span className="flex items-center gap-2">
            <Package className="w-4 h-4 text-amber-500" />
            {language === 'it' ? 'Scenari ipotetici' : 'What-if scenarios'}
            {scenarios.length > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full">
                {scenarios.length}
              </span>
            )}
          </span>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showScenarioPanel ? 'rotate-90' : ''}`} />
        </button>

        {showScenarioPanel && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-slate-700">
            {/* Quick-add presets */}
            <div className="flex flex-wrap gap-2 pt-3">
              {SCENARIO_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => addScenario(preset.label, preset.amount)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-700 transition-colors text-gray-600 dark:text-gray-300"
                >
                  <preset.icon className="w-3.5 h-3.5" />
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Active scenarios list */}
            {scenarios.length > 0 && (
              <div className="space-y-2">
                {scenarios.map((s) => (
                  <div
                    key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      s.enabled
                        ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/40'
                        : 'bg-gray-50 dark:bg-slate-700/30 border-gray-200 dark:border-slate-600 opacity-60'
                    }`}
                  >
                    {/* Toggle */}
                    <button
                      onClick={() => toggleScenario(s.id)}
                      className="shrink-0 text-gray-400 hover:text-amber-500 transition-colors"
                    >
                      {s.enabled
                        ? <ToggleRight className="w-5 h-5 text-amber-500" />
                        : <ToggleLeft className="w-5 h-5" />
                      }
                    </button>

                    {/* Name */}
                    <input
                      type="text"
                      value={s.name}
                      onChange={(e) => updateScenario(s.id, 'name', e.target.value)}
                      className="flex-1 min-w-0 bg-transparent border-none p-0 text-sm font-medium focus:ring-0 text-gray-800 dark:text-gray-200"
                    />

                    {/* Amount */}
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={s.amount}
                        onChange={(e) => updateScenario(s.id, 'amount', parseFloat(e.target.value) || 0)}
                        className={`w-28 text-right bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-md px-2 py-1 text-xs font-mono focus:ring-1 focus:ring-amber-400 focus:border-amber-400 ${
                          s.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                        }`}
                      />
                      <span className="text-[10px] text-gray-400">€</span>
                    </div>

                    {/* Month */}
                    <input
                      type="month"
                      value={s.month}
                      onChange={(e) => updateScenario(s.id, 'month', e.target.value)}
                      className="bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-md px-2 py-1 text-xs focus:ring-1 focus:ring-amber-400 focus:border-amber-400 text-gray-700 dark:text-gray-200 w-32"
                    />

                    {/* Delete */}
                    <button
                      onClick={() => removeScenario(s.id)}
                      className="shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {scenarios.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">
                {language === 'it'
                  ? 'Clicca su un preset per aggiungere uno scenario ipotetico al grafico'
                  : 'Click a preset to add a what-if scenario to the chart'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Main Chart — Agicap-style ── */}
      <div className="card !rounded-b-none border-b-0">
        <div className="px-2 pt-2">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} barGap={2} barCategoryGap="18%">
                <defs>
                  <linearGradient id="cashAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={isDark ? '#334155' : '#f1f5f9'} />
                <XAxis
                  dataKey="fullLabel"
                  tick={({ x, y, payload }) => {
                    const isCurrentMonth = chartData.find(d => d.fullLabel === payload.value)?.period === currentMonthStr
                    return (
                      <g transform={`translate(${x},${y})`}>
                        {isCurrentMonth && (
                          <rect x={-24} y={2} width={48} height={18} rx={4} fill={isDark ? '#1e40af' : '#2563eb'} />
                        )}
                        <text
                          x={0} y={14}
                          textAnchor="middle"
                          fill={isCurrentMonth ? '#ffffff' : axisColor}
                          fontSize={10}
                          fontWeight={isCurrentMonth ? 700 : 400}
                        >
                          {payload.value}
                        </text>
                      </g>
                    )
                  }}
                  tickLine={false}
                  axisLine={false}
                  height={24}
                />
                <YAxis
                  tickFormatter={(v) => formatCompact(v)}
                  tick={{ fontSize: 10, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                />
                <ReferenceLine y={0} stroke={isDark ? '#475569' : '#cbd5e1'} strokeWidth={1} />
                <Tooltip
                  cursor={{ fill: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.12)', radius: 4 }}
                  formatter={(value: number, name: string) => {
                    if (name === 'cashArea') return [null, null]
                    return [
                      `${formatCurrency(Math.abs(value))} €`,
                      name === 'inflows' ? (language === 'it' ? 'Entrate' : 'Inflows')
                        : name === 'outflows' ? (language === 'it' ? 'Uscite' : 'Outflows')
                        : name === 'scenarioPosition' ? (language === 'it' ? 'Scenario ipotetico' : 'What-if')
                        : (language === 'it' ? 'Posizione di cassa' : 'Cash position')
                    ]
                  }}
                  contentStyle={{
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    fontSize: '11px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                />
                <Bar dataKey="inflows" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={32} name="inflows" />
                <Bar dataKey="outflows" fill="#ef4444" radius={[0, 0, 3, 3]} maxBarSize={32} name="outflows" />
                <Area
                  type="monotone"
                  dataKey="cashPosition"
                  fill="url(#cashAreaGrad)"
                  stroke="none"
                  name="cashArea"
                />
                <Line
                  type="monotone"
                  dataKey="cashPosition"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 5, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                  name="cashPosition"
                />
                {scenarios.some(s => s.enabled) && (
                  <Line
                    type="monotone"
                    dataKey="scenarioPosition"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={{ r: 3, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 4, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }}
                    name="scenarioPosition"
                    connectNulls
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Wallet className="w-10 h-10 mb-3" />
              <p className="text-sm">{language === 'it' ? 'Nessun dato disponibile' : 'No data available'}</p>
              <p className="text-xs mt-1">{language === 'it' ? 'Carica un estratto conto per iniziare' : 'Upload a bank statement to start'}</p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-8 pt-4 pb-4 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 bg-emerald-500 rounded-sm" />
            {language === 'it' ? 'Entrate' : 'Inflows'}
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 bg-red-500 rounded-sm" />
            {language === 'it' ? 'Uscite' : 'Outflows'}
          </span>
          <span className="flex items-center gap-2">
            <span className="w-5 h-0.5 bg-blue-500 rounded" />
            {language === 'it' ? 'Posizione di cassa' : 'Cash position'}
          </span>
          {scenarios.some(s => s.enabled) && (
            <span className="flex items-center gap-2">
              <span className="w-5 h-0.5 bg-amber-500 rounded" style={{ borderTop: '2px dashed #f59e0b', height: 0 }} />
              {language === 'it' ? 'Scenario ipotetico' : 'What-if scenario'}
            </span>
          )}
        </div>
      </div>

      {/* ── Category search bar ── */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 border-t-0 px-4 py-2">
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            placeholder={language === 'it' ? 'Cerca categoria...' : 'Search category...'}
            className="bg-transparent border-none p-0 text-xs focus:ring-0 text-gray-700 dark:text-gray-200 placeholder-gray-400 flex-1"
          />
        </div>
      </div>

      {/* ── Financial Table — Agicap-style ── */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 border-t-0 rounded-b-xl overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700">
              <th className="sticky left-0 z-10 bg-white dark:bg-slate-800 text-left px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400 min-w-[200px]">
                {/* month headers */}
              </th>
              {periods.map((p) => {
                const [y, m] = p.period.split('-')
                const isCurrentMonth = p.period === currentMonthStr
                return (
                  <th
                    key={p.period}
                    className={`text-center px-3 py-2.5 font-semibold min-w-[120px] whitespace-nowrap transition-colors ${
                      isCurrentMonth
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : hoveredMonth === p.period
                        ? 'bg-gray-50 dark:bg-slate-700/50'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                    onMouseEnter={() => setHoveredMonth(p.period)}
                    onMouseLeave={() => setHoveredMonth(null)}
                  >
                    {`${months[parseInt(m) - 1].toUpperCase()} ${y.slice(2)}`}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {/* Tesoreria a inizio mese */}
            <tr className="border-b border-gray-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-700/30">
              <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-700/30 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <Wallet className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {language === 'it' ? 'Tesoreria a inizio mese' : 'Opening balance'}
                  </span>
                </div>
              </td>
              {periods.map((p) => {
                const isCurrentMonth = p.period === currentMonthStr
                return (
                  <td
                    key={p.period}
                    className={`text-right px-3 py-2 font-medium tabular-nums ${
                      isCurrentMonth
                        ? 'bg-blue-50 dark:bg-blue-900/30 font-bold text-gray-900 dark:text-white'
                        : hoveredMonth === p.period
                        ? 'bg-gray-50 dark:bg-slate-700/50'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                    onMouseEnter={() => setHoveredMonth(p.period)}
                    onMouseLeave={() => setHoveredMonth(null)}
                  >
                    {formatCurrency(p.statement.opening_balance)}
                  </td>
                )
              })}
            </tr>

            {/* ── ENTRATE (Inflows) ── */}
            <tr
              className="border-b border-gray-100 dark:border-slate-700/50 cursor-pointer hover:bg-green-50/50 dark:hover:bg-green-900/10"
              onClick={() => toggleRow('entrate')}
            >
              <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expandedRows.has('entrate') ? 'rotate-90' : ''}`} />
                  <span className="w-5 h-5 rounded bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                    <ArrowUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                  </span>
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {language === 'it' ? 'Entrate' : 'Inflows'}
                  </span>
                </div>
              </td>
              {periods.map((p) => {
                const total = p.statement.operating.total_inflows + p.statement.investing.total_inflows + p.statement.financing.total_inflows
                const isCurrentMonth = p.period === currentMonthStr
                return (
                  <td
                    key={p.period}
                    className={`text-right px-3 py-2.5 font-semibold tabular-nums text-green-600 dark:text-green-400 ${
                      isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30 font-bold' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                    }`}
                    onMouseEnter={() => setHoveredMonth(p.period)}
                    onMouseLeave={() => setHoveredMonth(null)}
                  >
                    {formatCurrency(total)}
                  </td>
                )
              })}
            </tr>

            {/* Entrate breakdown */}
            {expandedRows.has('entrate') && (
              <>
                {/* Area Operativa */}
                <tr className="border-b border-gray-50 dark:border-slate-700/30">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 pl-12 pr-4 py-1.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-400 uppercase text-[10px] font-semibold tracking-wider">
                        {language === 'it' ? 'AREA OPERATIVA' : 'OPERATING'}
                      </span>
                    </div>
                  </td>
                  {periods.map((p) => {
                    const val = p.statement.operating.total_inflows
                    const total = p.statement.operating.total_inflows + p.statement.investing.total_inflows + p.statement.financing.total_inflows
                    const pct = total > 0 ? Math.round((val / total) * 100) : 0
                    const isCurrentMonth = p.period === currentMonthStr
                    return (
                      <td
                        key={p.period}
                        className={`text-right px-3 py-1.5 tabular-nums ${
                          isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                        }`}
                        onMouseEnter={() => setHoveredMonth(p.period)}
                        onMouseLeave={() => setHoveredMonth(null)}
                      >
                        <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(val)}</span>
                        {pct > 0 && isCurrentMonth && (
                          <span className="ml-1 text-[9px] text-gray-400">{pct}%</span>
                        )}
                      </td>
                    )
                  })}
                </tr>

                {/* Area Finanziaria */}
                <tr className="border-b border-gray-50 dark:border-slate-700/30">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 pl-12 pr-4 py-1.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-400 uppercase text-[10px] font-semibold tracking-wider">
                        {language === 'it' ? 'AREA FINANZIARIA' : 'FINANCING'}
                      </span>
                    </div>
                  </td>
                  {periods.map((p) => {
                    const val = p.statement.financing.total_inflows
                    const isCurrentMonth = p.period === currentMonthStr
                    return (
                      <td
                        key={p.period}
                        className={`text-right px-3 py-1.5 tabular-nums text-gray-600 dark:text-gray-400 ${
                          isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                        }`}
                        onMouseEnter={() => setHoveredMonth(p.period)}
                        onMouseLeave={() => setHoveredMonth(null)}
                      >
                        {formatCurrency(val)}
                      </td>
                    )
                  })}
                </tr>

                {/* Area Investimenti */}
                <tr className="border-b border-gray-50 dark:border-slate-700/30">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 pl-12 pr-4 py-1.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-400 uppercase text-[10px] font-semibold tracking-wider">
                        {language === 'it' ? 'AREA INVESTIMENTI' : 'INVESTING'}
                      </span>
                    </div>
                  </td>
                  {periods.map((p) => {
                    const val = p.statement.investing.total_inflows
                    const isCurrentMonth = p.period === currentMonthStr
                    return (
                      <td
                        key={p.period}
                        className={`text-right px-3 py-1.5 tabular-nums text-gray-600 dark:text-gray-400 ${
                          isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                        }`}
                        onMouseEnter={() => setHoveredMonth(p.period)}
                        onMouseLeave={() => setHoveredMonth(null)}
                      >
                        {formatCurrency(val)}
                      </td>
                    )
                  })}
                </tr>
              </>
            )}

            {/* ── USCITE (Outflows) ── */}
            <tr
              className="border-b border-gray-100 dark:border-slate-700/50 cursor-pointer hover:bg-red-50/50 dark:hover:bg-red-900/10"
              onClick={() => toggleRow('uscite')}
            >
              <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expandedRows.has('uscite') ? 'rotate-90' : ''}`} />
                  <span className="w-5 h-5 rounded bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                    <ArrowDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                  </span>
                  <span className="font-semibold text-red-700 dark:text-red-400">
                    {language === 'it' ? 'Uscite' : 'Outflows'}
                  </span>
                </div>
              </td>
              {periods.map((p) => {
                const total = Math.abs(p.statement.operating.total_outflows + p.statement.investing.total_outflows + p.statement.financing.total_outflows)
                const isCurrentMonth = p.period === currentMonthStr
                return (
                  <td
                    key={p.period}
                    className={`text-right px-3 py-2.5 font-semibold tabular-nums text-red-600 dark:text-red-400 ${
                      isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30 font-bold' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                    }`}
                    onMouseEnter={() => setHoveredMonth(p.period)}
                    onMouseLeave={() => setHoveredMonth(null)}
                  >
                    {formatCurrency(total)}
                  </td>
                )
              })}
            </tr>

            {/* Uscite breakdown */}
            {expandedRows.has('uscite') && (
              <>
                {/* Area Operativa */}
                <tr className="border-b border-gray-50 dark:border-slate-700/30">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 pl-12 pr-4 py-1.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-400 uppercase text-[10px] font-semibold tracking-wider">
                        {language === 'it' ? 'AREA OPERATIVA' : 'OPERATING'}
                      </span>
                    </div>
                  </td>
                  {periods.map((p) => {
                    const val = Math.abs(p.statement.operating.total_outflows)
                    const isCurrentMonth = p.period === currentMonthStr
                    return (
                      <td
                        key={p.period}
                        className={`text-right px-3 py-1.5 tabular-nums text-red-600 dark:text-red-400 font-medium ${
                          isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                        }`}
                        onMouseEnter={() => setHoveredMonth(p.period)}
                        onMouseLeave={() => setHoveredMonth(null)}
                      >
                        {formatCurrency(val)}
                      </td>
                    )
                  })}
                </tr>

                {/* Area Finanziaria */}
                <tr className="border-b border-gray-50 dark:border-slate-700/30">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 pl-12 pr-4 py-1.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-400 uppercase text-[10px] font-semibold tracking-wider">
                        {language === 'it' ? 'AREA FINANZIARIA' : 'FINANCING'}
                      </span>
                    </div>
                  </td>
                  {periods.map((p) => {
                    const val = Math.abs(p.statement.financing.total_outflows)
                    const isCurrentMonth = p.period === currentMonthStr
                    return (
                      <td
                        key={p.period}
                        className={`text-right px-3 py-1.5 tabular-nums text-gray-600 dark:text-gray-400 ${
                          isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                        }`}
                        onMouseEnter={() => setHoveredMonth(p.period)}
                        onMouseLeave={() => setHoveredMonth(null)}
                      >
                        {formatCurrency(val)}
                      </td>
                    )
                  })}
                </tr>

                {/* Area Investimenti */}
                <tr className="border-b border-gray-50 dark:border-slate-700/30">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 pl-12 pr-4 py-1.5">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-400 uppercase text-[10px] font-semibold tracking-wider">
                        {language === 'it' ? 'AREA INVESTIMENTI' : 'INVESTING'}
                      </span>
                    </div>
                  </td>
                  {periods.map((p) => {
                    const val = Math.abs(p.statement.investing.total_outflows)
                    const isCurrentMonth = p.period === currentMonthStr
                    return (
                      <td
                        key={p.period}
                        className={`text-right px-3 py-1.5 tabular-nums text-gray-600 dark:text-gray-400 ${
                          isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                        }`}
                        onMouseEnter={() => setHoveredMonth(p.period)}
                        onMouseLeave={() => setHoveredMonth(null)}
                      >
                        {formatCurrency(val)}
                      </td>
                    )
                  })}
                </tr>
              </>
            )}

            {/* ── FLUSSO NETTO (Net Cash Flow) ── */}
            <tr className="border-t-2 border-gray-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30">
              <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-700/30 px-4 py-2.5">
                <span className="font-bold text-gray-800 dark:text-gray-200">
                  {language === 'it' ? 'Flusso netto' : 'Net cash flow'}
                </span>
              </td>
              {periods.map((p) => {
                const net = p.statement.net_cash_flow
                const isCurrentMonth = p.period === currentMonthStr
                return (
                  <td
                    key={p.period}
                    className={`text-right px-3 py-2.5 font-bold tabular-nums ${
                      net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    } ${isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''}`}
                    onMouseEnter={() => setHoveredMonth(p.period)}
                    onMouseLeave={() => setHoveredMonth(null)}
                  >
                    {net > 0 ? '+' : ''}{formatCurrency(net)}
                  </td>
                )
              })}
            </tr>

            {/* Tesoreria a fine mese */}
            <tr className="bg-slate-50 dark:bg-slate-700/30">
              <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-700/30 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <Wallet className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {language === 'it' ? 'Tesoreria a fine mese' : 'Closing balance'}
                  </span>
                </div>
              </td>
              {periods.map((p) => {
                const isCurrentMonth = p.period === currentMonthStr
                return (
                  <td
                    key={p.period}
                    className={`text-right px-3 py-2 font-bold tabular-nums text-gray-900 dark:text-white ${
                      isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/30' : hoveredMonth === p.period ? 'bg-gray-50 dark:bg-slate-700/50' : ''
                    }`}
                    onMouseEnter={() => setHoveredMonth(p.period)}
                    onMouseLeave={() => setHoveredMonth(null)}
                  >
                    {formatCurrency(p.statement.closing_balance)}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
